import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { User, Mail, Lock, Save, AlertCircle, CheckCircle, Camera, Calendar, Shield, MapPin, Plus, Edit2, Trash2, Check, X, Home, Phone } from "lucide-react";

const Profile = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [user, setUser] = useState({
    accountName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
    createdAt: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Address State
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressData, setAddressData] = useState({
    recipientName: "",
    phone: "",
    addressLine: "",
    district: "",
    province: "",
    postalCode: "",
    isDefault: false,
  });

  useEffect(() => {
    fetchProfile();
    fetchAddresses();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/auth/check-session`, {
        headers: { Authorization: token },
      });

      if (!response.ok) throw new Error("Failed to fetch profile");

      const data = await response.json();
      // Role mapping: ADMIN = ผู้ดูแลระบบ, SELLER = ผู้ขาย, USER = สมาชิกทั่วไป
      const roleMap = {
        ADMIN: "ผู้ดูแลระบบ",
        SELLER: "ผู้ขาย",
        USER: "สมาชิกทั่วไป"
      };
      const getRoleDisplay = (role) => {
        const roleUpper = (role || "").toUpperCase();
        return roleMap[roleUpper] || "สมาชิกทั่วไป"; // Default to สมาชิกทั่วไป if role not found
      };
      setUser((prev) => ({
        ...prev,
        accountName: data.accountName,
        email: data.email,
        role: getRoleDisplay(data.role),
        createdAt: new Date(data.createdAt).toLocaleDateString("th-TH", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      }));
      setLoading(false);
    } catch (err) {
      setError("ไม่สามารถดึงข้อมูลโปรไฟล์ได้");
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/addresses`, {
        headers: { Authorization: token },
      });
      if (!response.ok) throw new Error("Failed to fetch addresses");
      const data = await response.json();
      setAddresses(data);
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
    setMessage(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (user.password && user.password !== user.confirmPassword) {
      setError("รหัสผ่านไม่ตรงกัน");
      return;
    }

    if (user.password && user.password.length < 6) {
      setError("รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          accountName: user.accountName,
          password: user.password || undefined,
        }),
      });

      if (!response.ok) throw new Error("Failed to update profile");

      setMessage("บันทึกข้อมูลสำเร็จ!");
      setUser((prev) => ({ ...prev, password: "", confirmPassword: "" }));
    } catch (err) {
      setError("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
    } finally {
      setSaving(false);
    }
  };

  // Address Functions
  const handleAddressChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddressData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setAddressSaving(true);

    try {
      const token = localStorage.getItem("token");
      const url = editingAddressId
        ? `${API_BASE_URL}/api/addresses/${editingAddressId}`
        : `${API_BASE_URL}/api/addresses`;

      const response = await fetch(url, {
        method: editingAddressId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(addressData),
      });

      if (!response.ok) throw new Error("Failed to save address");

      await fetchAddresses();
      resetAddressForm();
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกที่อยู่");
    } finally {
      setAddressSaving(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm("คุณแน่ใจว่าต้องการลบที่อยู่นี้?")) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/addresses/${id}`, {
        method: "DELETE",
        headers: { Authorization: token },
      });

      if (!response.ok) throw new Error("Failed to delete address");
      setAddresses(addresses.filter((addr) => addr.addressId !== id));
    } catch (error) {
      alert("ลบที่อยู่ไม่สำเร็จ");
    }
  };

  const handleEditAddress = (address) => {
    setAddressData({
      recipientName: address.recipientName || "",
      phone: address.phone || "",
      addressLine: address.addressLine || "",
      district: address.district || "",
      province: address.province || "",
      postalCode: address.postalCode || "",
      isDefault: address.isDefault || false,
    });
    setEditingAddressId(address.addressId);
    setShowAddressForm(true);
  };

  const resetAddressForm = () => {
    setAddressData({
      recipientName: "",
      phone: "",
      addressLine: "",
      district: "",
      province: "",
      postalCode: "",
      isDefault: false,
    });
    setEditingAddressId(null);
    setShowAddressForm(false);
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Avatar */}
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-black flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                {user.accountName?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <button className="absolute bottom-0 right-0 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow border border-gray-200">
                <Camera size={16} className="text-gray-600" />
              </button>
            </div>

            <div className="text-center sm:text-left">
              <h1 className="text-2xl font-bold text-gray-900">{user.accountName}</h1>
              <p className="text-gray-500">{user.email}</p>
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-red-100 rounded-full">
                <Shield size={14} className="text-red-600" />
                <span className="text-sm font-medium text-red-900">{user.role}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab("profile")}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${activeTab === "profile"
                ? "text-red-600 border-b-2 border-red-600"
                : "text-gray-500 hover:text-gray-700"
                }`}
            >
              <User size={18} className="inline mr-2" />
              ข้อมูลส่วนตัว
            </button>
            <button
              onClick={() => setActiveTab("addresses")}
              className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${activeTab === "addresses"
                ? "text-red-600 border-b-2 border-red-600"
                : "text-gray-500 hover:text-gray-700"
                }`}
            >
              <MapPin size={18} className="inline mr-2" />
              ที่อยู่จัดส่ง ({addresses.length})
            </button>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-700">{message}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Info Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">ข้อมูลบัญชี</h2>

              <div className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ชื่อบัญชี
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="accountName"
                      value={user.accountName}
                      onChange={handleChange}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Email (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    อีเมล
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={user.email}
                      readOnly
                      className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">อีเมลไม่สามารถเปลี่ยนแปลงได้</p>
                </div>

                {/* Member Since */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">สมาชิกตั้งแต่</p>
                    <p className="font-medium text-gray-900">{user.createdAt}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Password Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">เปลี่ยนรหัสผ่าน</h2>

              <div className="space-y-5">
                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    รหัสผ่านใหม่
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      name="password"
                      value={user.password}
                      onChange={handleChange}
                      placeholder="กรอกรหัสผ่านใหม่หากต้องการเปลี่ยน"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ยืนยันรหัสผ่านใหม่
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={user.confirmPassword}
                      onChange={handleChange}
                      placeholder="ยืนยันรหัสผ่านใหม่อีกครั้ง"
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={saving}
              className="w-full flex items-center justify-center gap-3 py-4 px-8 bg-red-600 text-white rounded-lg font-semibold text-lg hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save size={20} />
                  บันทึกการเปลี่ยนแปลง
                </>
              )}
            </button>
          </form>
        )}

        {/* Address Tab */}
        {activeTab === "addresses" && (
          <div className="space-y-6">
            {/* Add Button */}
            {!showAddressForm && (
              <button
                onClick={() => setShowAddressForm(true)}
                className="w-full py-4 px-6 bg-white rounded-xl shadow-sm border-2 border-dashed border-gray-300 text-gray-600 hover:border-red-500 hover:text-red-600 transition-all flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                เพิ่มที่อยู่ใหม่
              </button>
            )}

            {/* Address Form */}
            {showAddressForm && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editingAddressId ? "แก้ไขที่อยู่" : "เพิ่มที่อยู่ใหม่"}
                  </h2>
                  <button
                    onClick={resetAddressForm}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X size={20} className="text-gray-600" />
                  </button>
                </div>

                <form onSubmit={handleAddressSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อผู้รับ</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="recipientName"
                          value={addressData.recipientName}
                          onChange={handleAddressChange}
                          required
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">เบอร์โทรศัพท์</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Phone className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          value={addressData.phone}
                          onChange={handleAddressChange}
                          required
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ที่อยู่</label>
                    <div className="relative">
                      <div className="absolute top-3 left-3 pointer-events-none">
                        <Home className="h-5 w-5 text-gray-400" />
                      </div>
                      <textarea
                        name="addressLine"
                        value={addressData.addressLine}
                        onChange={handleAddressChange}
                        required
                        rows={2}
                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none resize-none"
                        placeholder="เลขที่บ้าน, หมู่บ้าน, ซอย, ถนน"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">แขวง/ตำบล</label>
                      <input
                        type="text"
                        name="district"
                        value={addressData.district}
                        onChange={handleAddressChange}
                        required
                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">เขต/อำเภอ</label>
                      <input
                        type="text"
                        name="province"
                        value={addressData.province}
                        onChange={handleAddressChange}
                        required
                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">รหัสไปรษณีย์</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={addressData.postalCode}
                        onChange={handleAddressChange}
                        required
                        maxLength={5}
                        className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="isDefault"
                      id="isDefault"
                      checked={addressData.isDefault}
                      onChange={handleAddressChange}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isDefault" className="text-sm text-gray-700">
                      ตั้งเป็นที่อยู่เริ่มต้น
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={addressSaving}
                    className="w-full flex items-center justify-center gap-3 py-4 px-8 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all disabled:opacity-50"
                  >
                    {addressSaving ? (
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check size={20} />
                        {editingAddressId ? "บันทึกการเปลี่ยนแปลง" : "บันทึกที่อยู่"}
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Address List */}
            {addresses.length === 0 && !showAddressForm ? (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm">
                <MapPin className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">ไม่มีที่อยู่จัดส่ง</h3>
                <p className="text-gray-500">คุณยังไม่ได้เพิ่มที่อยู่สำหรับจัดส่งสินค้า</p>
              </div>
            ) : (
              <div className="space-y-4">
                {addresses.map((address) => (
                  <div
                    key={address.addressId}
                    className={`bg-white rounded-xl shadow-sm border-2 p-6 transition-all ${address.isDefault ? "border-red-500" : "border-gray-100"
                      }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{address.recipientName}</h3>
                          {address.isDefault && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                              <Check size={12} />
                              ค่าเริ่มต้น
                            </span>
                          )}
                        </div>
                        <p className="text-gray-600">{address.phone}</p>
                        <p className="text-gray-600 mt-1">
                          {address.addressLine}, {address.district}, {address.province} {address.postalCode}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditAddress(address)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteAddress(address.addressId)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
